import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Clock, 
  Shield, 
  Users, 
  ArrowRight,
  CheckCircle,
  Stethoscope
} from "lucide-react";

interface HomepageProps {
  onRoleSelect: (role: 'patient' | 'doctor') => void;
}

export function Homepage({ onRoleSelect }: HomepageProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Clean Navigation Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo matching sidebar style */}
            <div className="flex items-center space-x-3">
              <div style={{ height: '1.75rem' }}>
                <img src="/clinicaflow-logo.svg" alt="ClinicaFlow" className="h-full" />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Artistic and Flowing */}
      <section 
        className="relative py-24 px-4 overflow-hidden"
        style={{ 
          background: 'linear-gradient(90deg, hsla(208, 33%, 21%, 1) 0%, hsla(211, 36%, 46%, 1) 100%)' 
        }}
      >
        {/* Organic Background Shapes */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" 
               style={{ transform: 'translate(30%, -30%) scale(1.2)' }}></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-green-200 to-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-pulse" 
               style={{ transform: 'translate(-30%, 30%) scale(1.1)', animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-gradient-to-bl from-purple-100 to-pink-100 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse" 
               style={{ transform: 'translate(-50%, -50%) scale(0.9)', animationDelay: '4s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <div className="mb-8">
                <div className="inline-flex items-center px-6 py-3 bg-white/90 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium text-slate-700 mb-8 shadow-lg">
                  <Heart className="w-4 h-4 mr-2 text-red-500" />
                  Excellence in Every Connection
                  <span className="ml-2 text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">New</span>
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  <span className="text-white">Healthcare</span>
                  <br />
                  <span className="bg-gradient-to-r from-emerald-300 to-green-300 bg-clip-text text-transparent">
                    in every
                  </span>
                  <br />
                  <span className="text-white">connection</span>
                </h1>
              </div>
              
              <p className="text-lg text-slate-200 mb-8 max-w-lg leading-relaxed">
                Designed elegantly for healthcare providers who are the key to delivering 
                exceptional patient care and streamlining clinical workflows with modern technology.
              </p>
              
              {/* Two role-based buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => onRoleSelect('patient')}
                  size="lg"
                  className="bg-white hover:bg-gray-50 text-slate-800 text-lg px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group font-semibold"
                >
                  <span className="flex items-center">
                    <Heart className="mr-2 w-5 h-5 text-red-500" />
                    Enter as Patient
                  </span>
                </Button>
                
                <Button 
                  onClick={() => onRoleSelect('doctor')}
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group font-semibold"
                >
                  <span className="flex items-center">
                    <Stethoscope className="mr-2 w-5 h-5" />
                    Enter as Doctor
                  </span>
                </Button>
              </div>
            </div>

            {/* Right Visual Element */}
            <div className="relative">
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 backdrop-blur-sm bg-opacity-90">
                {/* Floating Cards */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-100">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">AI Triage Assessment</div>
                      <div className="text-sm text-gray-600">Smart patient prioritization</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Smart Scheduling</div>
                      <div className="text-sm text-gray-600">Automated appointment booking</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">HIPAA Compliant</div>
                      <div className="text-sm text-gray-600">Secure data protection</div>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-200 rounded-full opacity-80"></div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-green-200 rounded-full opacity-60"></div>
                <div className="absolute top-1/2 -right-2 w-4 h-4 bg-blue-200 rounded-full opacity-70"></div>
              </div>
            </div>
          </div>
          
          {/* Bottom metrics */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 opacity-90">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">$2B+</div>
              <div className="text-sm text-slate-300">Healthcare savings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">1M+</div>
              <div className="text-sm text-slate-300">Patients served</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">99.9%</div>
              <div className="text-sm text-slate-300">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">50+</div>
              <div className="text-sm text-slate-300">Healthcare integrations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Organic and Artistic */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 overflow-hidden">
        {/* Background organic shapes */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-green-200/30 to-emerald-200/30 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-tl from-emerald-200/30 to-teal-200/30 rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-white/70 backdrop-blur-sm border border-green-200 rounded-full text-sm font-medium text-green-700 mb-6">
              Why Choose us
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Healthcare is The Key to Your
              <br />
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Good Practice
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="group">
              <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-green-100">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mr-4">
                    <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="w-8 h-8 bg-green-200 rounded-full opacity-60"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">100% Fresh Healthcare</h3>
                <p className="text-gray-600 leading-relaxed">
                  Advanced triage systems that analyze patient data in real-time, 
                  ensuring the most critical cases receive immediate attention.
                </p>
                <div className="absolute top-4 right-4 w-3 h-3 bg-yellow-300 rounded-full"></div>
              </div>
            </div>

            {/* Feature Card 2 */}
            <div className="group">
              <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-100">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mr-4">
                    <Shield className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="w-6 h-6 bg-blue-200 rounded-full opacity-70"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Premium Security Standards</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our providers maintain the highest security standards to ensure 
                  your sensitive health data is protected with enterprise-grade encryption.
                </p>
                <div className="absolute bottom-4 right-4 w-4 h-4 bg-green-300 rounded-full opacity-80"></div>
              </div>
            </div>

            {/* Feature Card 3 */}
            <div className="group">
              <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-purple-100">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mr-4">
                    <Stethoscope className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="w-5 h-5 bg-purple-200 rounded-full opacity-60"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Clinical Intelligence</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our integrated AI systems use advanced algorithms that work synergistically 
                  to deliver high-quality clinical insights from raw patient data.
                </p>
                <div className="absolute top-1/2 left-4 w-2 h-2 bg-pink-300 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

     

      {/* CTA Section - Dual Access Points */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm font-medium text-green-700 mb-6">
              Delivery Process
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Access Point
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Streamlined experiences designed for both healthcare providers and patients, 
              delivered with the care and attention you deserve.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Healthcare Providers */}
            <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Stethoscope className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Healthcare Providers</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Comprehensive practice management with patient scheduling, 
                  clinical decision support, and workflow optimization tools.
                </p>
                <ul className="text-left text-sm text-gray-600 mb-6 space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    Patient management dashboard
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    AI-powered clinical insights
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    Integrated scheduling system
                  </li>
                </ul>
                <Button 
                  onClick={() => onRoleSelect('doctor')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 group"
                  size="lg"
                >
                  <span className="flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                    Access Provider Portal
                  </span>
                </Button>
              </CardContent>
            </Card>

            {/* Patients */}
            <Card className="border-2 border-green-200 hover:border-green-300 transition-colors bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Patients</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Take control of your healthcare journey with easy booking, 
                  digital triage, and secure access to your medical information.
                </p>
                <ul className="text-left text-sm text-gray-600 mb-6 space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    Online appointment booking
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    Digital health triage
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    Secure medical records
                  </li>
                </ul>
                <Button 
                  onClick={() => onRoleSelect('patient')}
                  variant="outline"
                  className="w-full border-2 border-green-600 text-green-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 hover:border-green-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 group"
                  size="lg"
                >
                  <span className="flex items-center justify-center">
                    <Heart className="w-5 h-5 mr-2 group-hover:scale-110 text-red-500 transition-transform" />
                    Access Patient Portal
                  </span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer - Artistic and Modern */}
      <footer className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-gray-900 text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-green-400/10 rounded-full filter blur-2xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-400/10 rounded-full filter blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-purple-400/10 rounded-full filter blur-xl transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Heart className="w-8 h-8 text-green-400" />
                <div className="text-2xl font-bold">ClinicaFlow</div>
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Transforming healthcare through intelligent technology and 
                beautiful design that puts patients first.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <div className="w-4 h-4 bg-current rounded-sm"></div>
                </div>
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <div className="w-4 h-4 bg-current rounded-full"></div>
                </div>
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <div className="w-4 h-4 bg-current rounded-sm"></div>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <h3 className="text-xl font-semibold mb-4">Ready to get started?</h3>
              <p className="text-gray-300 mb-6">
                Join thousands of healthcare providers already using ClinicaFlow
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => onRoleSelect('patient')}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Patient Portal
                </Button>
                <Button 
                  onClick={() => onRoleSelect('doctor')}
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Doctor Portal
                </Button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 text-center">
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-400">
              <span>© 2024 ClinicaFlow. All rights reserved.</span>
              <span>•</span>
              <span>HIPAA Compliant</span>
              <span>•</span>
              <span>SOC 2 Type II</span>
              <span>•</span>
              <span>EU GDPR Ready</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
